import React from "react";
import { Avatar, Box, Grid, Paper, Stack, TableContainer, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import CardHeaderTitle from "../../components/card-header-title/index.jsx";
import AppBarChart from "../../components/chart/bar.jsx";
import AppPieChart from "../../components/chart/pie.jsx";
import WPMChartComponent from "../../components/chart/wpm-chart.js";
import InfoCardBox from "../../components/info-card-box/index.js";
import InsightsCard from "../../components/insights/insights-card.js";
import { useThemeMode } from "../../components/theme/index.jsx";
import Topics from "../../components/topics/Topics.js";
import { toggleButtonStyle } from "../../consts.js";
import { THEME_MODES } from "../../helpers/constant.js";
import { getComparatorSort, removeDetectKeywordFromVisuals } from "../../helpers/index.js";
import VIcon from "../images/Logo_black.png";

function CampaignInsights({ analytics }) {
    const { colours, themeMode, FONT } = useThemeMode();
    const [pickedAgent, setPickedAgent] = React.useState("agent");

    // Cards Data
    const cardsData = [
        {
            icon: "user",
            title: "# of Agents",
            tooltip: "Number of agents assigned to this campaign",
            value: analytics.agent_count || 0,
            backgroundColor: colours.blue10,
            borderColor: colours.blue20,
            iconBg: colours.blue100,
        },
        {
            icon: "calendar",
            title: "Total Calls",
            tooltip: "Total calls made in this campaign",
            value: analytics.total_calls || 0,
            backgroundColor: colours.purple5,
            borderColor: colours.purple20,
            iconBg: colours.purple100,
        },
        {
            icon: "watch",
            title: "Avg. Call Duration",
            tooltip: "Average call duration for this campaign",
            value: analytics.duration,
            backgroundColor: colours.success5,
            borderColor: colours.success20,
            iconBg: colours.success100,
        },
        {
            customAvatar: <Avatar sx={(theme) => ({ bgcolor: theme.palette.mode === THEME_MODES.LIGHT ? colours.white : colours.black })}><img src={VIcon} alt="Vocalysd Avatar" loading="lazy" width={18} height={22} /></Avatar>,
            title: "Avg. Vocalysd Score",
            tooltip: "Average Vocalysd score for this campaign",
            customValue: <Typography variant="h5" component="div" fontWeight={600} color={colours.success100}>{analytics.vocalys_score || 0}%</Typography>,
            backgroundColor: colours.orange10,
            borderColor: colours.orange20,
        },
    ];

    return (
        <Grid
            className="vocalysed-home-upadted campaigns-insights"
            container
            spacing={0}
            direction="row"
            flexGrow={1}
            padding={3}
            gap={3}
            overflow="auto"
            alignItems="start"
            sx={{ overflowX: "hidden" }}
        >
            {/* Top Cards Section */}
            <Grid
                item
                xs={12}
                padding={3}
                sx={{ border: `1px solid ${colours.grayDark500}`, borderRadius: 2.5, boxShadow: "none", backgroundColor: colours.white }}
                component={Paper}
            >
                <Grid
                    container
                    spacing={3}
                    direction="row"
                >
                    {cardsData.map((card, index) => (
                        <Grid key={index} item xs={12} sm={6} lg={3}>
                            <InfoCardBox
                                colours={colours}
                                themeMode={themeMode}
                                icon={card.icon}
                                title={card.title}
                                tooltip={card.tooltip}
                                value={card.value}
                                backgroundColor={card.backgroundColor}
                                borderColor={card.borderColor}
                                iconBg={card.iconBg}
                                customAvatar={card.customAvatar}
                                customValue={card.customValue}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            <Grid
                container
                spacing={0}
                direction="row"
                gap={3}
            >
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="smile"
                                title="Sentiment"
                                tooltip="Sentiment graph showing the overall sentiment trends for this campaign"
                            />
                            <Box marginTop={2} height={150}>
                                <AppBarChart
                                    data={analytics.sentiment}
                                />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="alert-triangle"
                                title="Alerts"
                                tooltip="Campaign alerts showing critical updates and notifications"
                            />
                            {(!analytics.alerthits?.length) ? (<Stack fontSize={FONT.SIZE_14} height="100%" alignItems="center" justifyContent="center">
                                No Data Available
                            </Stack>) :
                                <Box marginTop={2}>
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {analytics.alerthits.map((z, zi) => (
                                            <Chip
                                                key={zi}
                                                label={removeDetectKeywordFromVisuals(z.alert)}
                                                size="small"
                                                color="warning"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            }
                        </Paper>
                    </Grid>
                    <InsightsCard data={analytics} colours={colours} themeMode={themeMode} />
                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={.5}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Top Questions"
                                tooltip="Top questions identified from campaign conversations, based on frequency"
                            />
                            <ButtonGroup
                                variant="contained"
                                aria-label="outlined primary button group"
                                className="buttons-border-extend"
                            >
                                <Button
                                    onClick={() => setPickedAgent("agent")}
                                    style={toggleButtonStyle(pickedAgent === "agent", colours, themeMode)}
                                >
                                    Agent
                                </Button>
                                <Button
                                    onClick={() => setPickedAgent("client")}
                                    style={toggleButtonStyle(pickedAgent === "client", colours, themeMode)}
                                >
                                    Client
                                </Button>
                            </ButtonGroup>
                        </Stack>
                        <Box marginTop={2}>
                            <TableContainer>
                                <Table size="small" aria-label="Top Questions">
                                    <TableBody>
                                        {analytics.questionhits[pickedAgent]
                                        .sort(getComparatorSort('desc', 'text'))
                                        .sort(getComparatorSort('asc', 'n'))
                                        .map((z, zi) => (
                                                <TableRow hover key={zi}>
                                                    <TableCell
                                                        style={{
                                                            paddingTop: 3,
                                                            paddingBottom: 3,
                                                        }}
                                                    >
                                                        {zi + 1 + ". " + z.text}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {!analytics.questionhits[pickedAgent].length ? (
                                            <TableRow>
                                                <TableCell>
                                                    No Data Available
                                                </TableCell>
                                            </TableRow>
                                        ) : ("")}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="pie-chart"
                                title="Speaker Analytics"
                                tooltip="Speaker analytics providing insights on individual speaker performance"
                            />
                            <Box height={166}>
                                <AppPieChart data={analytics.speaker_analysis} />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Avg. WPM Agent vs Client"
                                tooltip="Average words per minute (WPM) comparison between agent and client in this campaign"
                            />
                            <Box marginTop={1} height={150}>
                                <WPMChartComponent data={analytics.wpm} />
                            </Box>
                        </Paper>
                    </Grid>
                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <CardHeaderTitle
                            icon="message-circle"
                            title="Topics"
                            tooltip="The top topics discussed in this campaign based on conversation analysis"
                        />

                        <Topics
                            topicshits={analytics.topicshits}
                            maxItems={20}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default CampaignInsights;